import { environment } from '../../../environments/environment';

export const DEFAULT_CLIENT = 'ResilientGalicia';

export interface FooterLogo {
  url: string;
  alt: string;
  class?: string;
}

/** Enlace del menú de enlaces del footer. `label` es una clave de traducción. */
export interface FooterLink {
  label: string;
  url: string;
}

/**
 * Menú de enlaces del footer, ubicado a la izquierda junto al mensaje "Desarrollado por Valora".
 * Oculto por defecto: requiere `enabled: true` explícito para mostrarse.
 */
export interface FooterLinksMenuConfig {
  enabled?: boolean;
  links?: FooterLink[];
}

export interface MenuBarSocialLink {
  network: 'linkedin' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'training';
  url: string;
  title?: string;
  /** Si se define, solo se muestra para esos tipos de organización. */
  orgTypes?: ('company' | 'community' | 'cityhall' | 'xunta' | 'technicalOffice')[];
}

export interface MenuBarConfig {
  /** Logo por tipo de organización. La clave 'default' se usa como fallback. */
  orgLogos: Partial<Record<'company' | 'community' | 'cityhall' | 'xunta' | 'technicalOffice' | 'default', string>>;
  /** Mostrar selector de idioma. Omitir o `true` → visible. `false` → oculto. */
  languageSelector?: boolean;
  /** Códigos de idioma disponibles. Por defecto: `['es', 'gl']`. */
  languages?: string[];
  /** Enlaces a redes sociales. Si se omite, se usa el LinkedIn de Alianza para empresas. */
  socialLinks?: MenuBarSocialLink[];
}

const DEFAULT_MENU_BAR_LANGUAGES = ['es', 'gl'];

const DEFAULT_MENU_BAR_SOCIAL_LINKS: MenuBarSocialLink[] = [
  {
    network: 'linkedin',
    url: 'https://linkedin.com/company/alianza-galega-polo-clima/',
    title: 'Síguenos en LinkedIn',
    orgTypes: ['company'],
  },
];

/**
 * Visibilidad de las entradas del sidebar y submenú admin.
 * Omitir clave o `true` → visible. `false` → oculto.
 */
export interface MenuConfig {
  activities?: boolean;
  inscriptions?: boolean;
  attendees?: boolean;
  resources?: boolean;
  assistances?: boolean;
  newsletters?: boolean;
  freeContents?: boolean;
  users?: boolean;
  risks?: boolean;
  initiatives?: boolean;
  projects?: boolean;
  cityhalls?: boolean;
  companies?: boolean;
  communities?: boolean;
  // Submenú admin
  adminInvoiceLevels?: boolean;
  adminPopulationLevels?: boolean;
  adminSectors?: boolean;
  adminCommunityTypes?: boolean;
  adminIndicators?: boolean;
  adminCharacteristics?: boolean;
  adminLinesOfCredit?: boolean;
  adminForumCategories?: boolean;
  /** Moderación del foro (listado de temas y detalle) para oficina técnica. */
  forumModeration?: boolean;
  /** Pantalla de configuración de sellos (tipos y plantillas). */
  seals?: boolean;
  /** Pantalla de seguimiento y emisión de sellos por organización. */
  sealsTracking?: boolean;
}

/**
 * Visibilidad de las secciones de company-main-page.
 * Omitir clave o `true` → visible. `false` → oculto.
 */
export interface MainConfig {
  events?: boolean;
  calendar?: boolean;
  workshops?: boolean;
  youthRequest?: boolean;
  expertConsultation?: boolean;
  mentorshipRequest?: boolean;
  climateProfile?: boolean;
  resources?: boolean;
  strategy?: boolean;
  workTable?: boolean;
  documents?: boolean;
  /**
   * Pestaña "Mi documentación" dentro del apartado Documentos (empresa).
   * Omitir o `true` → visible. `false` → oculta (solo se muestra Informes/Modelos).
   */
  myDocumentation?: boolean;
  /** Módulo de Sellos para la organización (subir documentos y descargar sello). */
  seals?: boolean;
  /**
   * Visibilidad del módulo de Comunidad / Networking B2B.
   *  - `true` o ausente → visible para cualquier perfil autenticado.
   *  - `false` → oculto.
   *  - `string[]` → solo visible para los tipos de organización indicados
   *    (`'company' | 'cityhall' | 'xunta' | 'community' | 'technicalOffice'`).
   */
  community?: boolean | string[];
  /** Tipos de recurso excluidos de la lista. Si se omite o está vacío, se muestran todos. */
  excludedResourceTypes?: string[];
  /**
   * Muestra el selector de temática en el formulario de consulta a un experto.
   * Omitir o `true` → visible. `false` → oculto (el usuario solo escribe el mensaje).
   */
  expertConsultationTopicSelector?: boolean;
}

/**
 * Claves que, a diferencia del resto, están ocultas/deshabilitadas por defecto:
 * solo se activan si el entorno del cliente las declara explícitamente como `true`.
 */
const DEFAULT_HIDDEN_MENU_KEYS: ReadonlySet<keyof MenuConfig> = new Set(['seals']);
const DEFAULT_HIDDEN_MAIN_KEYS: ReadonlySet<keyof MainConfig> = new Set(['seals']);

/**
 * Devuelve `true` salvo que el valor sea explícitamente `false`, excepto para
 * las claves de `DEFAULT_HIDDEN_MENU_KEYS`, que están ocultas por defecto y
 * requieren `true` explícito para mostrarse.
 */
export function isMenuVisible(key: keyof MenuConfig): boolean {
  const value = (environment.menu as MenuConfig | undefined)?.[key];
  if (DEFAULT_HIDDEN_MENU_KEYS.has(key)) {
    return value === true;
  }
  return value !== false;
}

/**
 * Devuelve `true` salvo que el valor sea explícitamente `false`, excepto para
 * las claves de `DEFAULT_HIDDEN_MAIN_KEYS`, que están ocultas por defecto y
 * requieren `true` explícito para mostrarse.
 */
export function isMainVisible(key: keyof MainConfig): boolean {
  const value = (environment.main as MainConfig | undefined)?.[key];
  if (DEFAULT_HIDDEN_MAIN_KEYS.has(key)) {
    return value === true;
  }
  return value !== false;
}

/**
 * Visibilidad de una sección de `main` para un tipo de organización concreto.
 *   - Si la clave es booleana o está ausente, se aplica `isMainVisible()`.
 *   - Si la clave es un array de tipos, solo se considera visible cuando el
 *     `orgType` indicado figura en el array.
 */
export function isMainVisibleForOrgType(
  key: keyof MainConfig,
  orgType: string | undefined | null
): boolean {
  const value = (environment.main as MainConfig | undefined)?.[key];
  if (Array.isArray(value)) {
    return !!orgType && value.includes(orgType);
  }
  return value !== false;
}

/** Tipos de organización autorizados para una clave del bloque `main`. */
export function getMainAllowedOrgTypes(
  key: keyof MainConfig
): string[] | null {
  const value = (environment.main as MainConfig | undefined)?.[key];
  return Array.isArray(value) ? [...value] : null;
}

/** Keys de tipos de recurso excluidos según la configuración del cliente. */
export function getExcludedResourceTypes(): string[] {
  return (environment.main as MainConfig | undefined)?.excludedResourceTypes ?? [];
}

/** Devuelve `true` salvo que el valor sea explícitamente `false`. */
export function isExpertConsultationTopicSelectorVisible(): boolean {
  return (environment.main as MainConfig | undefined)?.expertConsultationTopicSelector !== false;
}

const ASSISTANCE_CARD_KEYS: (keyof MainConfig)[] = [
  'youthRequest',
  'expertConsultation',
  'mentorshipRequest',
];

/** Número de tarjetas de asistencia activas en la página principal. */
export function getAssistanceCardsCount(): number {
  return ASSISTANCE_CARD_KEYS.filter((key) => isMainVisible(key)).length;
}

/** Clase Tailwind del grid según tarjetas activas (1→100%, 2→50%, 3→33%). */
export function getAssistanceCardsGridClass(): string {
  switch (getAssistanceCardsCount()) {
    case 1:
      return 'lg:grid-cols-1';
    case 2:
      return 'lg:grid-cols-2';
    default:
      return 'lg:grid-cols-3';
  }
}

function getMenuBarConfig(): MenuBarConfig | undefined {
  return environment.menuBar as MenuBarConfig | undefined;
}

/** Devuelve `true` salvo que el valor sea explícitamente `false`. */
export function isMenuBarLanguageSelectorVisible(): boolean {
  return getMenuBarConfig()?.languageSelector !== false;
}

/** Idiomas configurados para el menú superior. */
export function getMenuBarLanguages(): string[] {
  const languages = getMenuBarConfig()?.languages;
  return languages?.length ? languages : DEFAULT_MENU_BAR_LANGUAGES;
}

/** Enlaces sociales visibles para el tipo de organización indicado. */
export function getMenuBarSocialLinks(
  orgType?: OrganizationUnitTypeForSocial
): MenuBarSocialLink[] {
  const links = getMenuBarConfig()?.socialLinks ?? DEFAULT_MENU_BAR_SOCIAL_LINKS;

  if (!orgType) {
    return links;
  }

  return links.filter(
    (link) => !link.orgTypes?.length || link.orgTypes.includes(orgType)
  );
}

type OrganizationUnitTypeForSocial = NonNullable<MenuBarSocialLink['orgTypes']>[number];

/**
 * Devuelve el identificador del cliente activo:
 *   - environment.publicClient si está definido
 *   - DEFAULT_CLIENT en caso contrario
 *
 * Se usa como namespace para assets, estilos y traducciones servidas
 * desde `public/assets/<cliente>/` y `public/i18n/<cliente>/`.
 */
export function resolveClient(): string {
  return environment.publicClient ?? DEFAULT_CLIENT;
}

/** Ruta base donde viven los assets del cliente: `/assets/<cliente>` */
export function assetsBase(): string {
  return `/assets/${resolveClient()}`;
}

/**
 * Logo de la cabecera pública. Usa `menuBar.orgLogos.default` del cliente.
 * Ruta absoluta (`/...` o `http...`) se respeta; la relativa cuelga de `assetsBase`.
 */
export function getPublicHeaderLogo(): { src: string; alt: string } {
  const path = getMenuBarConfig()?.orgLogos?.default ?? 'images/logos/alianza.png';
  const src = /^(\/|https?:)/.test(path) ? path : `${assetsBase()}/${path}`;
  return { src, alt: environment.appTitle };
}

/**
 * URL de un logo de footer.
 * Absoluta se usa tal cual; relativa se antepone `assetsBase/images/footer/`.
 */
export function resolveFooterLogoUrl(url: string): string {
  return /^(\/|https?:)/.test(url) ? url : `${assetsBase()}/images/footer/${url}`;
}

/** URL del favicon del cliente. Usa el valor de environment.favicon o cae en /favicon.ico */
export function faviconUrl(): string {
  return (environment as any).favicon ?? '/favicon.ico';
}

/** URL del CSS de tema específico del cliente */
export function stylesUrl(): string {
  return `${assetsBase()}/styles.css`;
}

/** Ruta del directorio i18n del cliente: `/i18n/<cliente>` */
export function i18nClientPath(): string {
  return `/i18n/${resolveClient()}`;
}

/** Ruta del directorio i18n base (común a todos los clientes) */
export function i18nBasePath(): string {
  return '/i18n/base';
}
